define('viewModels/privileged/base/BaseInstanceEditor',[
    'lib/knockout',
    'services/routeService',
    'util/errorCallbackFactory',
    'viewModels/privileged/base/BaseTemplateEditor',
    'models/config/SchemaModel',
    'viewModels/privileged/devices/SchemaForm',
    'util/SchemaPreprocessor',
    'services/toastService',
    'services/loggingService'
], function (
    ko,
    routeService,
    errorCallbackFactory,
    BaseTemplateEditor,
    SchemaModel,
    SchemaForm,
    SchemaPreprocessor,
    toasts,
    LOG
) {
    'use strict';

    var BaseInstanceEditor = BaseTemplateEditor.extend(function () {
        var self = this;

        /**
         * This will be true if the instance is linked to a template.
         */
        self.useTemplate = true;

        /**
         * The instance that is being updated.
         */
        self.instance = null;

        /**
         * The loading state for the instance data.
         */
        self.loadingInstance = ko.observable(false);

        /**
         * Loads the schema.
         */
        self.loadInstance = function (instanceUri) {
            self.loadingInstance(true);
            return this.configService.readInstance(instanceUri)
                .then(function (response) {
                    self.instance = response;
                    return response;
                })
                .finally(function () {
                    self.loadingInstance(false);
                });
        };

        var configureSchemaForms = function(namespace, schema, data) {
            var filter, instanceSchema, model;
            self.loadBehavior('instance')
                .then(function (behavior) {
                    filter = new SchemaPreprocessor(schema);
                    instanceSchema = filter.exclusionBehavior(behavior.instance);
                    model = new SchemaModel(instanceSchema, data);
                    self.schemaModel(model);
                    return self.loadBehavior('simple');
                })
                .then(function(behavior) {
                    filter = new SchemaPreprocessor(instanceSchema);
                    self.simpleSchemaForm(self.subView(SchemaForm, namespace, model, filter.inclusionBehavior(behavior.simple)));
                    self.advancedSchemaForm(self.subView(SchemaForm, namespace, model, filter.exclusionBehavior(behavior.simple)));
                })
                .catch(function(error) {
                    LOG.error("there was an error loading the behavior when configuring schema forms: " + (error.status ? error.status : error));
                });
        };


        self.showingSimpleSchemaForm = self.computed(function(){
            return self.simpleSchemaForm() && self.simpleSchemaForm().allControls.length > 0;
        });

        self.showingAdvancedSchemaForm = self.computed(function(){
            return self.advancedSchemaForm() && self.advancedSchemaForm().allControls.length > 0;
        });
        /**
         * Configures to create a new instance from a schema.
         */
        self.configureForCreateFromSchema = function (namespace, schemaUri) {
            self.loadSchema(schemaUri)
                .then(function (schema) {
                    configureSchemaForms(namespace, schema);
                })
                .catch(function(error) {
                    LOG.error("there was an error loading the schema when configuring create from schema : " + (error.status ? error.status : error));
                });
        };

        /**
         * Configures to create a new instance from a template.
         */
        self.configureForCreateFromTemplate = function (namespace, templateUri) {
            var loadedTemplate;
            self.loadTemplate(templateUri)
                .then(function (template) {
                    loadedTemplate = template;
                    return self.loadSchema(template.schema.selfUri);
                })
                .then(function (schema) {
                    configureSchemaForms(namespace, schema, loadedTemplate.properties);
                })
                .catch(function(error) {
                    LOG.error("there was an error loading the schema when configuring create from template : " + (error.status ? error.status : error));
                });
        };

        /**
         * Override
         */
        self.configureForCreate = function (namespace, uri) {
            if (self.useTemplate) {
                self.configureForCreateFromTemplate(namespace, uri);
            }
            else {
                self.configureForCreateFromSchema(namespace, uri);
            }
        };

        /**
         * Override
         */
        self.configureForUpdate = function (namespace, instanceUri) {
            self.isNew(false);
            var loadedInstance;
            self.loadInstance(instanceUri)
                .then(function (instance) {
                    loadedInstance = instance;
                    if (self.setup().canHaveTemplates && instance.template) {
                        return self.loadTemplate(instance.template.selfUri);
                    } else if (instance.schema) {
                        return instance;
                    }
                })
                .then(function(instance) {
                    return self.loadSchema(instance.schema.selfUri);
                })
                .then(function(schema) {
                    configureSchemaForms(namespace, schema, loadedInstance.properties);
                })
                .catch(function(error) {
                    LOG.error("there was an error when trying to load the instance templates and schema. status code: " + (error.status ? error.status : error));
                });

        };

        /**
         * Override
         */
        self.configure = function (options) {
            options = options ? options : {};

            self.isNew(true);
            self.setup(options.setup);

            var namespaces =  options.namespaces ? options.namespaces : options.namespace;

            // Configure for the correct entity context.
            if (options.instanceUri) {
                self.configureForUpdate(namespaces, options.instanceUri);
            }
            else if (options.templateUri) {
                self.useTemplate = true;
                self.configureForCreate(namespaces, options.templateUri);
            }
            else if (options.schemaUri) {
                self.useTemplate = false;
                self.configureForCreate(namespaces, options.schemaUri);
            }
            else {
                throw "Invalid initialization options in BaseInstanceEditor.configure()";
            }
        };

        /**
         * Creates a new instance based on a schema.
         */
        self.getCreateFromSchemaPayload = function () {
            return {
                name: self.setup().name(),
                properties: self.schemaModel().unwrap(),
                schema: {
                    selfUri: self.schema.id
                }
            };
        };

        /**
         * Creates a new instance based on a template.
         */
        self.getCreateFromTemplatePayload = function () {
            return {
                name: self.setup().name(),
                properties: self.schemaModel().unwrap(),
                template: {
                    selfUri: self.template.selfUri
                },
                schema: {
                    selfUri: self.schema.id
                }
            };
        };

        /**
         * Override
         */
        self.getCreatePayload = function () {
            if (self.useTemplate) {
                return this.getCreateFromTemplatePayload();
            }
            else {
                return this.getCreateFromSchemaPayload();
            }
        };

        /**
         * Override
         */
        self.getUpdatePayload = function () {
            if (self.template) {
                return {
                    name: self.setup().name(),
                    properties: self.schemaModel().unwrap(),
                    template: {
                        selfUri: self.template.selfUri
                    },
                    schema: {
                        selfUri: self.schema.id
                    }
                };
            }
            else {
                return {
                    name: self.setup().name(),
                    properties: self.schemaModel().unwrap(),
                    schema: {
                        selfUri: self.schema.id
                    }
                };
            }
        };

        /**
         * Override
         */
        self.save = function () {
            if (self.isValid()) {
                var deferred;
                self.loadingSaveButton(true);
                if (self.isNew()) {
                    deferred = self.configService.createInstance(this.getCreatePayload());
                }
                else {
                    deferred = self.configService.updateInstance(self.instance.selfUri, this.getUpdatePayload());
                }
                return deferred
                    .then(function () {
                        routeService.back();
                    })
                    .catch(errorCallbackFactory.createCallback({namespace: 'config.error' }))
                    .finally(function () {
                        self.loadingSaveButton(false);
                    });
            }
        };
    });

    return  BaseInstanceEditor;
});

